import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Check, Download, FileText, Loader2, Calculator, AlertCircle, Plus } from 'lucide-react';
import AddPosteDepenseModal from '@/components/AddPosteDepenseModal';

const ComptabiliteBudget = ({ 
  postesDepenses, 
  selectedYear, 
  coproId, 
  apiClient, 
  getErrorMessage,
  onRefresh,
  coproprietaires = []
}) => {
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [budgetPostes, setBudgetPostes] = useState({});
  const [showAddPosteModal, setShowAddPosteModal] = useState(false);
  const [addingPoste, setAddingPoste] = useState(false);

  useEffect(() => {
    fetchBudget();
  }, [selectedYear, coproId]);

  const fetchBudget = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/coproprietes/' + coproId + '/budget?annee=' + selectedYear);
      setBudget(response.data);
      const postes = {};
      (response.data.postes || []).forEach(p => {
        postes[p.poste_id] = p.montant_prevu;
      });
      setBudgetPostes(postes);
    } catch (error) {
      console.error('Error fetching budget:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMontantChange = (posteId, value) => {
    setBudgetPostes(prev => ({
      ...prev,
      [posteId]: parseFloat(value) || 0
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const postes = Object.entries(budgetPostes)
        .filter(([_, montant]) => montant > 0)
        .map(([poste_id, montant_prevu]) => ({ poste_id, montant_prevu }));
      
      await apiClient.post('/coproprietes/' + coproId + '/budget', {
        annee: selectedYear,
        postes: postes
      });
      toast.success('Budget sauvegardé');
      fetchBudget();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    if (!window.confirm('Valider le budget ? Cette action permettra de générer les appels de fonds prévisionnels.')) {
      return;
    }
    setValidating(true);
    try {
      await apiClient.post('/coproprietes/' + coproId + '/budget/valider?annee=' + selectedYear);
      toast.success('Budget validé ! Vous pouvez maintenant créer les appels de fonds prévisionnels.');
      fetchBudget();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la validation'));
    } finally {
      setValidating(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await apiClient.get('/coproprietes/' + coproId + '/budget/pdf?annee=' + selectedYear, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'budget_previsionnel_' + selectedYear + '.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors du téléchargement'));
    }
  };

  const handleAddPoste = async (posteData) => {
    setAddingPoste(true);
    try {
      await apiClient.post('/coproprietes/' + coproId + '/postes', posteData);
      toast.success(`Poste "${posteData.nom}" créé avec succès !`);
      setShowAddPosteModal(false);
      // Rafraîchir les données pour afficher le nouveau poste
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la création du poste'));
    } finally {
      setAddingPoste(false);
    }
  };

  const totalBudget = Object.values(budgetPostes).reduce((sum, v) => sum + (v || 0), 0);
  const trimestriel = totalBudget / 4;
  const isValidated = budget?.valide;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Budget prévisionnel {selectedYear}</h2>
          <p className="text-slate-500 text-sm">
            Définissez le budget par poste de dépenses pour l'année
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button 
            onClick={() => setShowAddPosteModal(true)} 
            variant="outline" 
            className="gap-2"
            data-testid="add-poste-depense-btn"
          >
            <Plus className="w-4 h-4" /> Ajouter un poste
          </Button>
          {isValidated && (
            <Button onClick={handleDownloadPdf} variant="outline" className="gap-2">
              <Download className="w-4 h-4" /> Télécharger PDF
            </Button>
          )}
        </div>
      </div>

      {isValidated && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
            <Check className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="font-semibold text-emerald-800">Budget validé</p>
            <p className="text-sm text-emerald-600">
              Rendez-vous dans "Appels de fonds" pour créer les appels prévisionnels trimestriels.
            </p>
          </div>
        </div>
      )}

      {!isValidated && totalBudget === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-amber-600" />
          <p className="text-amber-800">
            Saisissez les montants prévisionnels pour chaque poste de dépenses, puis validez le budget.
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full min-w-[400px]">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-6 py-4 text-left font-semibold">Poste de dépenses</th>
              <th className="px-6 py-4 text-right font-semibold w-48">Montant annuel prévu</th>
            </tr>
          </thead>
          <tbody>
            {postesDepenses.map(poste => (
              <tr key={poste.id} className="border-t hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium">{poste.nom}</p>
                      {poste.description && (
                        <p className="text-sm text-slate-500">{poste.description}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={budgetPostes[poste.id] || ''}
                      onChange={(e) => handleMontantChange(poste.id, e.target.value)}
                      disabled={isValidated}
                      placeholder="0.00"
                      className="w-32 px-3 py-2 border rounded-lg text-right disabled:bg-slate-100 disabled:cursor-not-allowed"
                    />
                    <span className="text-slate-500">€</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-slate-50 rounded-xl border p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-slate-500 text-sm mb-1">Budget annuel total</p>
            <p className="text-3xl font-bold text-slate-900">{totalBudget.toFixed(2)} €</p>
          </div>
          <div className="text-center border-x border-slate-200">
            <p className="text-slate-500 text-sm mb-1">Appel trimestriel</p>
            <p className="text-3xl font-bold text-emerald-600">{trimestriel.toFixed(2)} €</p>
          </div>
          <div className="text-center">
            <p className="text-slate-500 text-sm mb-1">Nombre de postes</p>
            <p className="text-3xl font-bold text-slate-900">
              {Object.values(budgetPostes).filter(v => v > 0).length}
            </p>
          </div>
        </div>
      </div>

      {!isValidated && (
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <Button 
            onClick={handleSave} 
            disabled={saving || totalBudget === 0}
            variant="outline"
            className="gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Sauvegarder le brouillon
          </Button>
          <Button 
            onClick={handleValidate} 
            disabled={validating || totalBudget === 0}
            className="bg-emerald-600 hover:bg-emerald-700 gap-2"
          >
            {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Valider le budget
          </Button>
        </div>
      )}

      {/* Modal d'ajout de poste */}
      <AddPosteDepenseModal
        isOpen={showAddPosteModal}
        onClose={() => setShowAddPosteModal(false)}
        coproprietaires={coproprietaires}
        existingPostes={postesDepenses}
        onSubmit={handleAddPoste}
        isSubmitting={addingPoste}
      />
    </div>
  );
};

export default ComptabiliteBudget;
